import { Trans, t } from "@lingui/macro";
import { useLingui } from "@lingui/react";
import {
	ActionIcon,
	Anchor,
	Button,
	Card,
	Center,
	Container,
	Drawer,
	Flex,
	Group,
	Image,
	Loader,
	Modal,
	Paper,
	Rating,
	Select,
	SimpleGrid,
	Stack,
	Text,
	TextInput,
	Title,
} from "@mantine/core";
import { useNavigate } from "@remix-run/react";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
import {
	Clock,
	Filter,
	LogOut,
	MapPin,
	Phone,
	RotateCcw,
	Search,
	Stethoscope,
	X,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ClientOnly } from "remix-utils/client-only";
import { useCompanyConfig } from "~/hooks/useCompanyConfig";
import { usePortalAuth } from "~/hooks/usePortalAuth";
import { usePortalHydration } from "~/hooks/usePortalHydration";
import { usePortalState } from "~/utils/states";
import { PortalHeader } from "./portal-header";
import PortalHomepage from "./portal-homepage";
import PortalLoginModal from "./portal-login-modal";
import { PortalPinReminderBanner } from "./portal-pin-reminder-banner";

dayjs.extend(duration);

interface ActiveBooking {
	booking_id: string;
	booking_kode: string;
	booking_nama: string;
	booking_tanggal: string;
	booking_unit_nama: string;
	booking_dokter_nama: string;
	booking_bayar_nama: string;
	booking_noantrian: number;
	booking_noantrian_jam_awal: string;
	booking_noantrian_jam_akhir: string;
	jam_layanan_awal?: string;
	jam_layanan_akhir?: string;
	tenant_id: string;
	tenant_name: string;
	payment_link?: string;
	antrean_saat_ini: number;
}

interface BranchData {
	tenant_id: string;
	name: string;
	type: string;
	address: string;
	city: string;
	province: string;
	phone: string;
	services: string[];
	operating_hours: string;
	operational_status: string;
	status_message?: string;
	coordinates: {
		lat: number;
		lng: number;
	};
	rating?: number;
	total_reviews?: number;
	image_url: string;
	booking_url: string;
}

interface PortalLandingProps {
	onSelectBranch: (branch: BranchData) => void;
	tenantId: string;
}

export default function PortalLanding({
	onSelectBranch,
	tenantId,
}: PortalLandingProps) {
	const { _ } = useLingui();
	const navigate = useNavigate();

	// All hooks must be at the top level - before any early returns
	const [searchQuery, setSearchQuery] = useState("");
	const [selectedProvince, setSelectedProvince] = useState<string | null>(null);
	const [selectedCity, setSelectedCity] = useState<string | null>(null);
	const [selectedType, setSelectedType] = useState<string | null>(null);
	const [selectedService, setSelectedService] = useState<string | null>(null);
	const [showFilters, setShowFilters] = useState(false);
	const [filterModalOpened, setFilterModalOpened] = useState(false);
	const [branches, setBranches] = useState<BranchData[]>([]);
	const [provinces, setProvinces] = useState<string[]>([]);
	const [cities, setCities] = useState<string[]>([]);
	const [serviceTypes, setServiceTypes] = useState<string[]>([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [loginModalOpened, setLoginModalOpened] = useState(false);

	// Pagination state
	const [currentPage, setCurrentPage] = useState(1);
	const itemsPerPage = 10;

	// Active bookings state
	const [activeBookings, setActiveBookings] = useState<ActiveBooking[]>([]);
	const [loadingBookings, setLoadingBookings] = useState(false);
	const activeBookingsFetched = useRef(false);

	// Wait for hydration to complete
	const hydrated = usePortalHydration();

	// Portal authentication with auto-refresh (only after hydration)
	usePortalAuth();

	// Portal authentication state
	const {
		isAuthenticated,
		portalPatient,
		authToken,
		logout,
		tokenExpiry,
		extendSession,
		selectedTenant,
		setSelectedTenant,
		isPortalFlow,
		canStartBooking,
		resetToMenu,
	} = usePortalState();

	// Fetch branches from API
	const fetchBranchesData = useCallback(async () => {
		try {
			setLoading(true);
			setError(null);

			const params = new URLSearchParams();
			if (selectedProvince) params.set("province", selectedProvince);
			if (selectedCity) params.set("city", selectedCity);
			if (selectedType) params.set("type", selectedType);
			if (selectedService) params.set("service", selectedService);
			if (searchQuery) params.set("search", searchQuery);
			// Don't set operational_status to show all branches by default

			const response = await fetch(`/api/branches?${params.toString()}`, {
				headers: {
					"x-tenant-id": tenantId,
				},
			});

			if (!response.ok) {
				throw new Error("Failed to fetch branches");
			}

			const result = await response.json();

			if (result.success) {
				setBranches(result.data || []);
				if (result.meta) {
					setProvinces(result.meta.provinces || []);
					setServiceTypes(result.meta.services || []);
					if (selectedProvince && result.meta.cities) {
						setCities(result.meta.cities);
					}
				}
			} else {
				throw new Error(result.error || "Failed to fetch branches");
			}
		} catch (err) {
			console.error("Error fetching branches:", err);
			setError(err instanceof Error ? err.message : "Unknown error");
			setBranches([]);
		} finally {
			setLoading(false);
		}
	}, [
		tenantId,
		selectedProvince,
		selectedCity,
		selectedType,
		selectedService,
		searchQuery,
	]);

	// Single effect for all data fetching with debounce - ONLY for branch selection mode
	useEffect(() => {
		// Only fetch branches if user is NOT authenticated (branch selection mode)
		// If authenticated, we show menu instead
		if (isAuthenticated) return;

		// Reset to page 1 when search changes
		setCurrentPage(1);

		const timeoutId = setTimeout(() => {
			fetchBranchesData();
		}, 300); // Debounce API calls

		return () => clearTimeout(timeoutId);
	}, [fetchBranchesData, isAuthenticated]);

	// Fetch active bookings when authenticated
	useEffect(() => {
		if (!isAuthenticated || !hydrated) {
			activeBookingsFetched.current = false;
			return;
		}

		const globalPatientId = portalPatient?.global_patient_id;
		const nik = portalPatient?.nik;
		const dob = portalPatient?.date_of_birth;

		if (!globalPatientId || !dob) {
			console.warn("Missing required patient data");
			return;
		}

		// Prevent duplicate calls
		if (activeBookingsFetched.current) {
			return;
		}

		activeBookingsFetched.current = true;

		const fetchActiveBookings = async () => {
			try {
				setLoadingBookings(true);

				const response = await fetch("/api/portal/active-bookings", {
					method: "POST",
					headers: {
						"Content-Type": "application/json",
						"x-tenant-id": tenantId,
						...(authToken && { "portal-access-token": authToken }),
					},
					body: JSON.stringify({
						global_patient_id: globalPatientId,
						nik: nik || null,
						dob: dob,
					}),
				});

				if (response.ok) {
					const result = await response.json();
					if (result.success) {
						setActiveBookings(result.data || []);
					}
				}
			} catch (error) {
				console.error("Error fetching active bookings:", error);
				setActiveBookings([]);
			} finally {
				setLoadingBookings(false);
			}
		};

		fetchActiveBookings();
	}, [
		isAuthenticated,
		hydrated,
		tenantId,
		authToken,
		portalPatient?.global_patient_id,
		portalPatient?.nik,
		portalPatient?.date_of_birth,
	]);

	const handleSelectBranchForSync = (nikData: {
		nik: string;
		dateOfBirth: string;
	}) => {
		navigate("/portal-sync", {
			state: { syncMode: true, syncData: nikData },
		});
	};

	const handlePortalBranchSelection = (branch: BranchData) => {
		// Set selected tenant context for portal booking flow
		const tenantData = {
			tenant_id: branch.tenant_id,
			name: branch.name,
			type: branch.type,
			address: branch.address,
			city: branch.city,
			province: branch.province,
			phone: branch.phone,
			services: branch.services,
			operating_hours: branch.operating_hours,
			operational_status: branch.operational_status,
			coordinates: branch.coordinates,
			booking_url: branch.booking_url,
		};

		setSelectedTenant(tenantData);

		// Also store manually as backup in case zustand persistence fails
		try {
			localStorage.setItem("manual-portal-tenant", JSON.stringify(tenantData));
		} catch (e) {
			console.error("Error storing manual backup:", e);
		}

		// Give zustand time to persist to localStorage before navigating
		setTimeout(() => {
			const stored = localStorage.getItem("portal-auth-storage");
			const manual = localStorage.getItem("manual-portal-tenant");

			if (stored) {
				try {
					const parsed = JSON.parse(stored);
				} catch (e) {
					console.error("Error parsing after set:", e);
				}
			}

			// Stay on the same page since menu will be shown automatically
		}, 200); // Give zustand time to persist
	};

	// Update cities when province changes (without fetching)
	// biome-ignore lint/correctness/useExhaustiveDependencies: We only want to reset city when province changes
	useEffect(() => {
		setSelectedCity(null);
	}, [selectedProvince]);

	const resetFilters = () => {
		setSearchQuery("");
		setSelectedProvince(null);
		setSelectedCity(null);
		setSelectedType(null);
		setSelectedService(null);
	};

	// Get unique values for filters from branches
	const uniqueProvinces = useMemo(() => {
		const provinceSet = Array.from(new Set(branches.map((b) => b.province)));
		return provinceSet.sort();
	}, [branches]);

	const uniqueCities = useMemo(() => {
		const citySet = Array.from(
			new Set(
				branches
					.filter((b) => !selectedProvince || b.province === selectedProvince)
					.map((b) => b.city),
			),
		);
		return citySet.sort();
	}, [branches, selectedProvince]);

	const uniqueTypes = useMemo(() => {
		const typeSet = Array.from(new Set(branches.map((b) => b.type)));
		return typeSet.sort();
	}, [branches]);

	const uniqueServices = useMemo(() => {
		const allServices = branches.flatMap((b) => b.services);
		const serviceSet = Array.from(new Set(allServices));
		return serviceSet.sort();
	}, [branches]);

	// Filter branches based on search query and advanced filters
	const filteredBranches = useMemo(() => {
		return branches.filter((branch) => {
			// Search filter
			if (searchQuery.trim()) {
				const query = searchQuery.toLowerCase();
				const matchesSearch =
					branch.name.toLowerCase().includes(query) ||
					branch.address.toLowerCase().includes(query) ||
					branch.city.toLowerCase().includes(query) ||
					branch.province.toLowerCase().includes(query) ||
					branch.type.toLowerCase().includes(query);
				if (!matchesSearch) return false;
			}

			// Advanced filters
			if (selectedProvince && branch.province !== selectedProvince)
				return false;
			if (selectedCity && branch.city !== selectedCity) return false;
			if (selectedType && branch.type !== selectedType) return false;
			if (selectedService && !branch.services.includes(selectedService))
				return false;

			return true;
		});
	}, [
		branches,
		searchQuery,
		selectedProvince,
		selectedCity,
		selectedType,
		selectedService,
	]);

	// Count active filters
	const activeFilterCount = [
		selectedProvince,
		selectedCity,
		selectedType,
		selectedService,
	].filter(Boolean).length;

	const handleResetFilters = () => {
		setSelectedProvince(null);
		setSelectedCity(null);
		setSelectedType(null);
		setSelectedService(null);
	};

	const handleApplyFilters = () => {
		setFilterModalOpened(false);
	};

	// Reset city when province changes
	useEffect(() => {
		if (selectedProvince && selectedCity) {
			const cityExistsInProvince = branches.some(
				(b) => b.province === selectedProvince && b.city === selectedCity,
			);
			if (!cityExistsInProvince) {
				setSelectedCity(null);
			}
		}
	}, [selectedProvince, selectedCity, branches]);

	// Reset to page 1 when search query or filters change
	// biome-ignore lint/correctness/useExhaustiveDependencies: We want to reset page when filters change
	useEffect(() => {
		setCurrentPage(1);
	}, [
		searchQuery,
		selectedProvince,
		selectedCity,
		selectedType,
		selectedService,
	]);

	// Get dynamic company configuration from API
	const { companyConfig } = useCompanyConfig();
	const portalConfig = {
		title: companyConfig.portalTitle,
		subtitle: companyConfig.subtitle,
		primaryColor: companyConfig.colors.primary,
		secondaryColor: companyConfig.colors.secondary,
		accentColor: companyConfig.colors.accent,
		bgGradient: companyConfig.colors.bgGradient,
	};

	// Show loading until hydrated - AFTER all hooks are declared
	if (!hydrated) {
		return (
			<Container size="xl" py="xl">
				<Center style={{ minHeight: "50vh" }}>
					<Loader size="lg" />
				</Center>
			</Container>
		);
	}

	// Show menu page if user is authenticated (no need for selectedTenant)
	if (isAuthenticated) {
		return (
			<div style={{ minHeight: "100vh", background: "#f9fafb" }}>
				<PortalHeader showLanguageSelector={true} allowUnauthenticated={true} />
				<PortalHomepage activeBookings={activeBookings} />
			</div>
		);
	}

	return (
		<div style={{ minHeight: "100vh", background: "#f5f5f5" }}>
			{/* Use unified PortalHeader component */}
			<PortalHeader showLanguageSelector={true} allowUnauthenticated={true} />

			{/* PIN Reminder Banner - Show if user is authenticated but hasn't set PIN */}
			{isAuthenticated &&
				portalPatient &&
				(portalPatient.has_pin === false ||
					portalPatient.has_pin === undefined) && <PortalPinReminderBanner />}

			<Container size="xl" py={{ base: "md", sm: "xl" }} px="md">
				{/* Page Title */}
				<Title
					order={1}
					size="h2"
					fw={600}
					c="#1e3a8a"
					ta="center"
					mb="xl"
					style={{ fontSize: "clamp(20px, 5vw, 28px)" }}
				>
					<Trans>Daftar Fasilitas Kesehatan</Trans>
				</Title>

				{/* Search and Filter */}
				<Group justify="center" mb="xl" gap="md" wrap="nowrap" px="md">
					<TextInput
						placeholder={_(t`Cari faskes disini`)}
						value={searchQuery}
						onChange={(e) => setSearchQuery(e.currentTarget.value)}
						leftSection={<Search size={18} color="#9ca3af" />}
						style={{ flex: 1, maxWidth: "350px" }}
						styles={{
							input: {
								borderRadius: "25px",
								border: "1px solid #e5e7eb",
								padding: "10px 16px 10px 40px",
							},
						}}
					/>
					<div style={{ position: "relative", flexShrink: 0 }}>
						<Button
							variant="outline"
							leftSection={<Filter size={18} />}
							onClick={() => setFilterModalOpened(true)}
							style={{
								borderRadius: "25px",
								border: "1px solid #e5e7eb",
								color: "#374151",
							}}
						>
							<Trans>Filter</Trans>
						</Button>
						{activeFilterCount > 0 && (
							<div
								style={{
									position: "absolute",
									top: "-8px",
									right: "-8px",
									background: "#dc2626",
									color: "#ffffff",
									minWidth: "20px",
									height: "20px",
									borderRadius: "50%",
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
									fontSize: "12px",
									fontWeight: "bold",
									lineHeight: "1",
									padding: "0 5px",
									boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
								}}
							>
								{activeFilterCount}
							</div>
						)}
					</div>
				</Group>

				{/* Branch Grid */}
				{loading ? (
					<Center py="xl">
						<Stack align="center" gap="md">
							<Loader size="lg" color="tm-blue" />
							<Text c="dimmed">
								<Trans>Memuat data cabang...</Trans>
							</Text>
						</Stack>
					</Center>
				) : error ? (
					<Paper p="xl" ta="center">
						<Text c="red" size="lg" fw={600} mb="xs">
							<Trans>Error: {error}</Trans>
						</Text>
						<Text c="dimmed" mb="md">
							<Trans>Gagal memuat data cabang</Trans>
						</Text>
						<Button variant="outline" onClick={fetchBranchesData}>
							<Trans>Coba Lagi</Trans>
						</Button>
					</Paper>
				) : filteredBranches.length === 0 ? (
					<Paper p="xl" ta="center">
						<Stethoscope
							size={48}
							color="gray"
							style={{ margin: "0 auto 16px" }}
						/>
						<Title order={3} c="dimmed" mb="xs">
							<Trans>Tidak ada cabang ditemukan</Trans>
						</Title>
						<Text c="dimmed">
							{searchQuery || activeFilterCount > 0 ? (
								<Trans>Tidak ada hasil yang sesuai dengan pencarian Anda</Trans>
							) : (
								<Trans>Coba ubah filter atau kata kunci pencarian</Trans>
							)}
						</Text>
					</Paper>
				) : (
					<>
						<SimpleGrid cols={{ base: 1, sm: 2, lg: 3, xl: 5 }} spacing="md">
							{filteredBranches
								.slice(
									(currentPage - 1) * itemsPerPage,
									currentPage * itemsPerPage,
								)
								.map((branch) => (
									<Card
										key={branch.tenant_id}
										shadow="xs"
										padding="lg"
										radius="lg"
										onClick={() => {
											if (branch.operational_status === "open") {
												handlePortalBranchSelection(branch);
												navigate("/portal-unit", {
													state: { fromPortal: true },
												});
											}
										}}
										style={{
											background: "white",
											border: "1px solid #e5e7eb",
											transition: "all 0.2s ease",
											cursor:
												branch.operational_status === "open"
													? "pointer"
													: "not-allowed",
											opacity: branch.operational_status === "open" ? 1 : 0.6,
										}}
										onMouseEnter={(e) => {
											if (branch.operational_status === "open") {
												e.currentTarget.style.transform = "translateY(-4px)";
												e.currentTarget.style.boxShadow =
													"0 8px 16px rgba(0, 0, 0, 0.08)";
											}
										}}
										onMouseLeave={(e) => {
											e.currentTarget.style.transform = "translateY(0)";
											e.currentTarget.style.boxShadow =
												"0 1px 3px 0 rgba(0, 0, 0, 0.05)";
										}}
									>
										<Stack gap="sm" style={{ height: "100%" }}>
											{/* Logo */}
											<div
												style={{
													width: "100%",
													height: "60px",
													display: "flex",
													alignItems: "center",
													justifyContent: "flex-start",
												}}
											>
												<Image
													src={
														branch.image_url
															? `https://${branch.tenant_id}/pelayanan1/uploads/${branch.image_url}`
															: "/logo.webp"
													}
													alt={branch.name}
													h={50}
													fit="contain"
													onError={(e) => {
														e.currentTarget.src = "/logo.webp";
													}}
												/>
											</div>

											{/* Name */}
											<Text
												fw={600}
												size="md"
												c="#2c3e50"
												lineClamp={2}
												style={{
													fontSize: "16px",
												}}
											>
												{branch.name}
											</Text>

											{/* Address */}
											<Text
												size="sm"
												c="#6c757d"
												lineClamp={2}
												style={{
													fontSize: "13px",
												}}
											>
												{branch.address}
											</Text>

											{/* Badges */}
											<Group gap="xs">
												<div
													style={{
														background: "#f0f0f0",
														padding: "4px 12px",
														borderRadius: "4px",
														fontSize: "12px",
														color: "#495057",
													}}
												>
													{branch.type}
												</div>
												<div
													style={{
														background:
															branch.operational_status === "open"
																? "#d4edda"
																: "#f8d7da",
														padding: "4px 12px",
														borderRadius: "4px",
														fontSize: "12px",
														color:
															branch.operational_status === "open"
																? "#155724"
																: "#721c24",
													}}
												>
													{branch.operational_status === "open"
														? "Open"
														: "Closed"}
												</div>
											</Group>

											{/* Info with icons */}
											<Stack gap="xs" style={{ flex: 1 }}>
												<Group gap="xs" wrap="nowrap">
													<MapPin
														size={16}
														color="#6c757d"
														style={{ flexShrink: 0 }}
													/>
													<Text size="xs" c="#6c757d" lineClamp={1}>
														{branch.city}, {branch.province}
													</Text>
												</Group>

												<Group gap="xs" wrap="nowrap">
													<Phone
														size={16}
														color="#6c757d"
														style={{ flexShrink: 0 }}
													/>
													<Text size="xs" c="#6c757d">
														{branch.phone}
													</Text>
												</Group>

												<Group gap="xs" wrap="nowrap">
													<Clock
														size={16}
														color="#6c757d"
														style={{ flexShrink: 0 }}
													/>
													<Text size="xs" c="#6c757d">
														{branch.operating_hours || "24 Jam"}
													</Text>
												</Group>
											</Stack>

											{/* Button */}
											<Button
												fullWidth
												size="sm"
												disabled={branch.operational_status !== "open"}
												onClick={(e) => {
													e.stopPropagation();
													handlePortalBranchSelection(branch);
													navigate("/portal-unit", {
														state: { fromPortal: true },
													});
												}}
												style={{
													background:
														branch.operational_status === "open"
															? "#1e3a8a"
															: "#e5e7eb",
													color:
														branch.operational_status === "open"
															? "white"
															: "#9ca3af",
													border: "none",
													fontWeight: 500,
												}}
											>
												<Trans>Pilih Lokasi</Trans>
											</Button>
										</Stack>
									</Card>
								))}
						</SimpleGrid>

						{/* Pagination */}
						{filteredBranches.length > itemsPerPage && (
							<Center mt="xl">
								<Group gap="xs">
									{/* First page button */}
									<ActionIcon
										variant="subtle"
										color="gray"
										size="lg"
										onClick={() => setCurrentPage(1)}
										disabled={currentPage === 1}
									>
										<span style={{ fontSize: "14px" }}>«</span>
									</ActionIcon>

									{/* Previous button */}
									<ActionIcon
										variant="subtle"
										color="gray"
										size="lg"
										onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
										disabled={currentPage === 1}
									>
										<span style={{ fontSize: "14px" }}>‹</span>
									</ActionIcon>

									{/* Page numbers */}
									{Array.from({
										length: Math.ceil(filteredBranches.length / itemsPerPage),
									}).map((_, idx) => {
										const pageNum = idx + 1;
										const totalPages = Math.ceil(
											filteredBranches.length / itemsPerPage,
										);

										// Show first page, last page, current page, and pages around current
										if (
											pageNum === 1 ||
											pageNum === totalPages ||
											(pageNum >= currentPage - 1 && pageNum <= currentPage + 1)
										) {
											return (
												<ActionIcon
													key={pageNum}
													variant={
														currentPage === pageNum ? "filled" : "subtle"
													}
													color={currentPage === pageNum ? "blue" : "gray"}
													size="lg"
													onClick={() => setCurrentPage(pageNum)}
													style={{
														background:
															currentPage === pageNum
																? "#1e3a8a"
																: "transparent",
													}}
												>
													{pageNum}
												</ActionIcon>
											);
										}
										if (
											pageNum === currentPage - 2 ||
											pageNum === currentPage + 2
										) {
											return (
												<Text key={pageNum} c="gray.5">
													...
												</Text>
											);
										}
										return null;
									})}

									{/* Next button */}
									<ActionIcon
										variant="subtle"
										color="gray"
										size="lg"
										onClick={() =>
											setCurrentPage((p) =>
												Math.min(
													Math.ceil(filteredBranches.length / itemsPerPage),
													p + 1,
												),
											)
										}
										disabled={
											currentPage ===
											Math.ceil(filteredBranches.length / itemsPerPage)
										}
									>
										<span style={{ fontSize: "14px" }}>›</span>
									</ActionIcon>

									{/* Last page button */}
									<ActionIcon
										variant="subtle"
										color="gray"
										size="lg"
										onClick={() =>
											setCurrentPage(
												Math.ceil(filteredBranches.length / itemsPerPage),
											)
										}
										disabled={
											currentPage ===
											Math.ceil(filteredBranches.length / itemsPerPage)
										}
									>
										<span style={{ fontSize: "14px" }}>»</span>
									</ActionIcon>
								</Group>
							</Center>
						)}
					</>
				)}

				{/* Simple Footer */}
				<div
					style={{
						marginTop: "2rem",
						textAlign: "center",
						padding: "1rem 0",
						borderTop: "1px solid #e5e7eb",
					}}
				>
					<Text c="gray.5" size="xs">
						<Trans>Powered by Trustmedis</Trans>
					</Text>
				</div>
			</Container>

			{/* Login Modal */}
			<PortalLoginModal
				opened={loginModalOpened}
				onClose={() => setLoginModalOpened(false)}
				tenantId={tenantId}
				onSelectBranchForSync={handleSelectBranchForSync}
			/>

			{/* Filter Modal */}
			<Modal
				opened={filterModalOpened}
				onClose={() => setFilterModalOpened(false)}
				title={
					<Group gap="xs">
						<Filter size={20} />
						<Text fw={600}>
							<Trans>Filter</Trans>
						</Text>
					</Group>
				}
				size="md"
				centered
			>
				<Stack gap="md">
					{/* Province */}
					<Select
						label="Provinsi"
						placeholder="Pilih Provinsi"
						data={uniqueProvinces}
						value={selectedProvince}
						onChange={setSelectedProvince}
						clearable
						searchable
					/>

					{/* City */}
					<Select
						label="Kota"
						placeholder="Pilih Kota"
						data={uniqueCities}
						value={selectedCity}
						onChange={setSelectedCity}
						clearable
						searchable
						disabled={!selectedProvince}
					/>

					{/* Type */}
					<Select
						label="Tipe Faskes"
						placeholder="Pilih Tipe"
						data={uniqueTypes}
						value={selectedType}
						onChange={setSelectedType}
						clearable
						searchable
					/>

					{/* Service */}
					<Select
						label="Poli/Layanan"
						placeholder="Pilih Poli"
						data={uniqueServices}
						value={selectedService}
						onChange={setSelectedService}
						clearable
						searchable
					/>

					{/* Actions */}
					<Group justify="space-between" mt="md">
						<Button
							variant="outline"
							leftSection={<RotateCcw size={16} />}
							onClick={handleResetFilters}
							disabled={activeFilterCount === 0}
						>
							<Trans>Atur Ulang</Trans>
						</Button>
						<Button onClick={handleApplyFilters}>
							<Trans>Terapkan</Trans>
						</Button>
					</Group>
				</Stack>
			</Modal>
		</div>
	);
}
